/**
 * Desktop Form Responses Table Component
 * 
 * Features:
 * - Config-driven columns
 * - Sorting support
 * - Loading/Empty/Error states
 * - Checkbox selection per row + Select All
 * - Header delete icon for bulk delete
 * - Actions column with individual delete per row
 * - Responsive layout
 */

import React, { useMemo } from 'react';
import { ChevronUp, ChevronDown, AlertCircle, Trash2 } from 'lucide-react';
import { formatResponseForTable } from '../utils/formResponseFormatters';

const DesktopFormResponsesTable = ({
    responses = [],
    columns = [],
    loading = false,
    error = null,
    onRowClick = null,
    page = 0,
    pageSize = 10,
    totalItems = 0,
    sortBy = null,
    sortOrder = 'desc',
    onSort = null,
    onNextPage = null,
    onPrevPage = null,
    propertiesMap = {},
    portfolioName = '',
    // Selection props
    selectedIds = [],
    onSelectionChange = null,
    onBulkDelete = null,
    onSingleDelete = null,
}) => {
    // Format responses for display with source name context
    const formatContext = { propertiesMap, portfolioName };
    const formattedResponses = useMemo(
        () => responses.map(r => formatResponseForTable(r, formatContext)),
        [responses, propertiesMap, portfolioName]
    );

    // Default columns if not provided
    const defaultColumns = [
        { key: 'name', label: 'Contact Name', width: '200px', sortable: true },
        { key: 'email', label: 'Email', width: '220px', sortable: true },
        { key: 'source', label: 'Source', width: '150px', sortable: true },
        { key: 'submittedAtFormatted', label: 'Submitted', width: '150px', sortable: true },
    ];

    const columnsToUse = columns.length > 0 ? columns : defaultColumns;
    const totalPages = Math.ceil(totalItems / pageSize);
    const currentPage = page + 1;

    // Selection helpers
    const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
    const allPageSelected = formattedResponses.length > 0 && formattedResponses.every(r => selectedSet.has(r.id));
    const somePageSelected = formattedResponses.some(r => selectedSet.has(r.id));

    const handleSelectAll = () => {
        if (!onSelectionChange) return;
        const pageIds = formattedResponses.map(r => r.id);
        if (allPageSelected) {
            // Deselect all on this page
            onSelectionChange(selectedIds.filter(id => !pageIds.includes(id)));
        } else {
            // Select all on this page (merge with existing)
            const merged = new Set([...selectedIds, ...pageIds]);
            onSelectionChange([...merged]);
        }
    };

    const handleToggle = (id) => {
        if (!onSelectionChange) return;
        if (selectedSet.has(id)) {
            onSelectionChange(selectedIds.filter(i => i !== id));
        } else {
            onSelectionChange([...selectedIds, id]);
        }
    };

    // Handle row click
    const handleRowClick = (response) => {
        if (onRowClick) {
            onRowClick(response);
        }
    };

    // Handle sort
    const handleSort = (key) => {
        if (onSort) {
            onSort(key);
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="bg-white border border-notion-border rounded-lg overflow-hidden">
                <div className="p-8 text-center">
                    <div className="inline-block">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-stone-700 mx-auto mb-3"></div>
                        <p className="text-sm text-stone-600">Loading form responses...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="bg-white border border-red-200 rounded-lg overflow-hidden">
                <div className="p-6 flex items-center gap-3 bg-red-50">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <div>
                        <p className="text-sm font-medium text-red-900">Error loading responses</p>
                        <p className="text-xs text-red-700 mt-0.5">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    // Empty state
    if (formattedResponses.length === 0) {
        return (
            <div className="bg-white border border-notion-border rounded-lg overflow-hidden">
                <div className="p-12 text-center">
                    <div className="w-12 h-12 rounded-full bg-stone-100 flex items-center justify-center mx-auto mb-3">
                        <svg className="w-6 h-6 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                    </div>
                    <p className="text-sm font-medium text-stone-900">No form responses yet</p>
                    <p className="text-xs text-stone-500 mt-1">Responses will appear here when submissions are received</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white border border-notion-border rounded-lg overflow-hidden shadow-card">
            <div className="overflow-x-auto">
                <table className="w-full text-left">
                    <thead className="bg-stone-50 border-b border-notion-border">
                        <tr>
                            {/* Select-all checkbox */}
                            <th className="py-3 px-3 w-10">
                                <input
                                    type="checkbox"
                                    checked={allPageSelected}
                                    ref={(el) => { if (el) el.indeterminate = somePageSelected && !allPageSelected; }}
                                    onChange={handleSelectAll}
                                    className="rounded border-stone-300 text-stone-800 focus:ring-stone-500 cursor-pointer"
                                />
                            </th>
                            {columnsToUse.map((column) => (
                                <th
                                    key={column.key}
                                    onClick={() => column.sortable && handleSort(column.key)}
                                    className={`py-3 px-4 text-[10px] font-semibold text-stone-600 uppercase tracking-wider ${column.sortable ? 'cursor-pointer hover:bg-stone-100' : ''
                                        }`}
                                    style={{ width: column.width }}
                                >
                                    <div className="flex items-center gap-1.5">
                                        {column.label}
                                        {column.sortable && sortBy === column.key && (
                                            sortOrder === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                                        )}
                                    </div>
                                </th>
                            ))}
                            {/* Actions column header with bulk delete icon */}
                            <th className="py-3 px-3 w-14 text-right">
                                <button
                                    onClick={onBulkDelete}
                                    disabled={selectedIds.length === 0}
                                    title={selectedIds.length === 0 ? 'Select responses to delete' : `Delete ${selectedIds.length} selected`}
                                    className={`inline-flex items-center justify-center w-7 h-7 rounded transition-colors ${selectedIds.length > 0
                                            ? 'text-red-600 hover:bg-red-50 hover:text-red-700'
                                            : 'text-stone-300 cursor-not-allowed'
                                        }`}
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-notion-border">
                        {formattedResponses.map((response) => (
                            <tr
                                key={response.id}
                                onClick={() => handleRowClick(response)}
                                className={`text-xs ${onRowClick ? 'hover:bg-notion-sidebar cursor-pointer' : ''} ${selectedSet.has(response.id) ? 'bg-stone-50' : ''} transition-colors`}
                            >
                                {/* Row checkbox */}
                                <td className="px-3 py-3 w-10" onClick={(e) => e.stopPropagation()}>
                                    <input
                                        type="checkbox"
                                        checked={selectedSet.has(response.id)}
                                        onChange={() => handleToggle(response.id)}
                                        className="rounded border-stone-300 text-stone-800 focus:ring-stone-500 cursor-pointer"
                                    />
                                </td>
                                {columnsToUse.map((column) => {
                                    let value = response[column.key];

                                    // Custom render function
                                    if (column.render) {
                                        value = column.render(response);
                                    }

                                    return (
                                        <td key={column.key} className="p-3 text-stone-700" style={{ width: column.width }}>
                                            {value || '—'}
                                        </td>
                                    );
                                })}
                                {/* Actions cell */}
                                <td className="px-3 py-3 w-14 text-right" onClick={(e) => e.stopPropagation()}>
                                    <button
                                        onClick={() => onSingleDelete?.(response.id)}
                                        title="Delete response"
                                        className="inline-flex items-center justify-center w-7 h-7 rounded text-stone-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="px-4 py-3 border-t border-notion-border flex items-center justify-between text-xs">
                    <p className="text-stone-600">
                        Showing <span className="font-medium">{page * pageSize + 1}</span> to{' '}
                        <span className="font-medium">{Math.min((page + 1) * pageSize, totalItems)}</span> of{' '}
                        <span className="font-medium">{totalItems}</span> responses
                    </p>
                    <div className="flex gap-2">
                        <button
                            onClick={onPrevPage}
                            disabled={page === 0}
                            className="px-3 py-1.5 border border-notion-border rounded text-xs font-medium text-stone-600 hover:bg-notion-sidebar disabled:opacity-50 disabled:cursor-not-allowed transition"
                        >
                            Previous
                        </button>
                        <div className="px-3 py-1.5 text-stone-600 text-xs">
                            Page {currentPage} of {totalPages}
                        </div>
                        <button
                            onClick={onNextPage}
                            disabled={page >= totalPages - 1}
                            className="px-3 py-1.5 border border-notion-border rounded text-xs font-medium text-stone-600 hover:bg-notion-sidebar disabled:opacity-50 disabled:cursor-not-allowed transition"
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DesktopFormResponsesTable;
